import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Container,
  Modal,
  Paper,
  Skeleton,
  Snackbar,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material'
import { useEffect, useState } from 'react'
import axios, { pluckData } from 'src/utilities/http-client'
import { MuiMarkdown } from 'mui-markdown'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import { BarChart } from '@mui/x-charts'
import {
  fetchCourses,
  selectCourseByID,
  selectCourses,
  selectCoursesStatus,
  selectedCourseChanged,
  selectSelectedCourse,
} from './scribe.slice'
import { SearchIcon } from '../core/icons'

type MonthlyLearnersDataset = {
  month: string
  nurse: number
  paramedic: number
  emt: number
  aemt: number
  other: number
}[]

type YearlyLearnersDataset = {
  year: string
  nurse: number
  paramedic: number
  emt: number
  aemt: number
  other: number
}[]

type AllTimeLearnersDataset = {
  nurse: number
  paramedic: number
  emt: number
  aemt: number
  other: number
}[]

type SentimentAnalyses = {
  id: string
  reportID: string
  entityID: string
  licenseType: string
  analysisMonth: string
  analysisText: string
  createdTime: string
  updatedTime: string
}[]

type GroupToggleValue = 'Nurse' | 'EMT' | 'Paramedic' | 'AEMT' | 'Other'
type LearnersChartToggleValue = 'Monthly' | 'Yearly' | 'All Time'
type LikertChartToggleValue = '30Day' | '6Month' | 'Year' | 'All Time'

type LikertAverages = {
  monthYear: string
  topicsThoroughAndInteresting: number
  presenterGoodUnderstanding: number
  videoQualityProfessional: number
  benefitCareerGoals: number
  knowledgePositiveImpactPractice: number
  activityPositiveImpactPractice: number
}[]

export default () => {
  let dispatch = useAppDispatch()

  let courses = useAppSelector(selectCourses)
  let coursesStatus = useAppSelector(selectCoursesStatus)
  let [selectedSentimentAnalysisGroup, setSelectedSentimentAnalysisGroup] =
    useState<GroupToggleValue>('Nurse')
  let [open, setOpen] = useState(false)
  let [sentimentAnalysisModalOpen, setSentimentAnalysisModalOpen] =
    useState(false)

  let selectedCourse = useAppSelector(selectSelectedCourse)
  let course = useAppSelector(selectCourseByID(selectedCourse))

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  let [searchValue, setSearchValue] = useState<string>('')
  let [monthlyLearners, setMonthlyLearners] = useState<MonthlyLearnersDataset>(
    [],
  )
  let [yearlyLearners, setYearlyLearners] = useState<YearlyLearnersDataset>([])
  let [allTimeLearners, setAllTimeLearners] = useState<AllTimeLearnersDataset>(
    [],
  )
  let [sentimentAnalyses, setSentimentAnalyses] = useState<SentimentAnalyses>(
    [],
  )
  let [likertAverages, setLikertAverages] = useState<LikertAverages>([])
  let [yearLikertAverages, setYearLikertAverages] = useState<LikertAverages>([])
  let [sixMonthLikertAverages, setSixMonthLikertAverages] =
    useState<LikertAverages>([])
  let [allTimeLikertAverages, setAllTimeLikertAverages] =
    useState<LikertAverages>([])
  let [selectedGroup, setSelectedGroup] = useState<GroupToggleValue>('Nurse')
  let [selectedLearnerChart, setSelectedLearnerChart] =
    useState<LearnersChartToggleValue>('Monthly')
  let [selectedLikertChart, setSelectedLikertChart] =
    useState<LikertChartToggleValue>('30Day')
  let [errorMessage, setErrorMessage] = useState<string>('')

  useEffect(() => {
    if (coursesStatus === 'uninitialized') {
      dispatch(fetchCourses())
    }
  }, [coursesStatus])

  let getLikertAverages = () =>
    Promise.resolve().then(() =>
      axios
        .post('/api/scribe/course/likert-averages', {
          id: course?.id,
          kind: course?.kind,
          courseName: course?.name,
          courseCode: course?.courseCode,
          group: selectedGroup,
        })
        .then(pluckData)
        .then((res) => setLikertAverages(res))
        .catch((err) =>
          setErrorMessage(
            `Likert Averages Failed: ${err.response?.data || err}`,
          ),
        ),
    )

  let getSixMonthLikertAverages = () =>
    Promise.resolve().then(() =>
      axios
        .post('/api/scribe/course/likert-averages/six-months', {
          id: course?.id,
          kind: course?.kind,
          courseName: course?.name,
          courseCode: course?.courseCode,
          group: selectedGroup,
        })
        .then(pluckData)
        .then((res) => setSixMonthLikertAverages([res]))
        .catch((err) =>
          setErrorMessage(
            `Likert Averages Failed: ${err.response?.data || err}`,
          ),
        ),
    )

  let getYearLikertAverages = () =>
    Promise.resolve().then(() =>
      axios
        .post('/api/scribe/course/likert-averages/year', {
          id: course?.id,
          kind: course?.kind,
          courseName: course?.name,
          courseCode: course?.courseCode,
          group: selectedGroup,
        })
        .then(pluckData)
        .then((res) => setYearLikertAverages([res]))
        .catch((err) =>
          setErrorMessage(
            `Likert Averages Failed: ${err.response?.data || err}`,
          ),
        ),
    )

  let getAllTimeLikertAverages = () =>
    Promise.resolve().then(() =>
      axios
        .post('/api/scribe/course/likert-averages/all-time', {
          id: course?.id,
          kind: course?.kind,
          courseName: course?.name,
          courseCode: course?.courseCode,
          group: selectedGroup,
        })
        .then(pluckData)
        .then((res) => setAllTimeLikertAverages([res]))
        .catch((err) =>
          setErrorMessage(
            `Likert Averages Failed: ${err.response?.data || err}`,
          ),
        ),
    )

  let getLearnerCountsByMonth = () =>
    axios
      .post('/api/scribe/course/learner-counts/month', {
        id: course?.id,
        kind: course?.kind,
        courseName: course?.name,
        courseCode: course?.courseCode,
        group: selectedGroup,
      })
      .then(pluckData)
      .then((res) => setMonthlyLearners(res))
      .catch((err) =>
        setErrorMessage(
          `Monthly Learners Failed: ${err.response?.data || err}`,
        ),
      )

  let getLearnerCountsByYear = () =>
    axios
      .post('/api/scribe/course/learner-counts/year', {
        id: course?.id,
        kind: course?.kind,
        courseName: course?.name,
        courseCode: course?.courseCode,
        group: selectedGroup,
      })
      .then(pluckData)
      .then((res) => setYearlyLearners(res))
      .catch((err) =>
        setErrorMessage(`Yearly Learners Failed: ${err.response?.data || err}`),
      )

  let getAllTimeCounts = () =>
    axios
      .post('/api/scribe/course/learner-counts/all-time', {
        id: course?.id,
        kind: course?.kind,
        courseName: course?.name,
        courseCode: course?.courseCode,
        group: selectedGroup,
      })
      .then(pluckData)
      .then((res) => setAllTimeLearners(res))
      .catch((err) =>
        setErrorMessage(
          `All Time Learners Failed: ${err.response?.data || err}`,
        ),
      )
  let getSentimentAnalyses = () =>
    axios
      .post('/api/scribe/course/sentiment-analyses', {
        id: course?.id,
        kind: course?.kind,
        courseName: course?.name,
        courseCode: course?.courseCode,
        group: selectedGroup,
      })
      .then(pluckData)
      .then((res) => setSentimentAnalyses(res))
      .catch((err) =>
        setErrorMessage(
          `Sentiment Analyses failed: ${err.response?.data || err}`,
        ),
      )

  useEffect(() => {
    if (course && !monthlyLearners.length) {
      getLearnerCountsByMonth()
      getLearnerCountsByYear()
      getAllTimeCounts()
      getSentimentAnalyses()
    }
  }, [course])

  useEffect(() => {
    if (course && selectedGroup) {
      getLikertAverages()
      getSixMonthLikertAverages()
      getYearLikertAverages()
      getAllTimeLikertAverages()
    }
  }, [course, selectedGroup])

  let handleSelectedGroupChange = (
    _: React.MouseEvent<HTMLElement>,
    newGroup: string | null,
  ) => {
    setSelectedGroup(newGroup as GroupToggleValue)
    setLikertAverages([])
    setYearLikertAverages([])
    setSixMonthLikertAverages([])
  }

  let handleSelectedLearnerChartChange = (
    _: React.MouseEvent<HTMLElement>,
    newChart: string | null,
  ) => {
    setSelectedLearnerChart(newChart as LearnersChartToggleValue)
  }

  let handleSelectedLikertChartChange = (
    _: React.MouseEvent<HTMLElement>,
    newChart: string | null,
  ) => {
    setSelectedLikertChart(newChart as LikertChartToggleValue)
  }

  let handleSelectedSentimentGroupChange = (
    _: React.MouseEvent<HTMLElement>,
    newChart: string | null,
  ) => {
    setSelectedSentimentAnalysisGroup(newChart as GroupToggleValue)
  }

  return (
    <>
      <Container maxWidth="xl">
        <Typography variant="h1" sx={{ py: 4 }}>
          Accreditation Reporting
        </Typography>
        <Autocomplete
          open={open}
          onOpen={() => {
            setOpen(true)
          }}
          onClose={() => {
            setOpen(false)
          }}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          getOptionLabel={(option) => `${option.name} | ${option.id}`}
          onInputChange={(_, value) => {
            dispatch(selectedCourseChanged(value.split(' | ')[1]))
            setMonthlyLearners([])
            setYearlyLearners([])
            setLikertAverages([])
            setSentimentAnalyses([])
            setSixMonthLikertAverages([])
            setYearLikertAverages([])
            setAllTimeLikertAverages([])
          }}
          options={courses}
          loading={coursesStatus === 'pending'}
          renderInput={(params) => (
            <TextField
              {...params}
              onChange={(e) => setSearchValue(e.target.value)}
              label="Select a Course"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {<SearchIcon sx={{ cursor: 'pointer' }} />}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
        {course && (
          <>
            <Box className="grid grid-cols-6 gap-4">
              <Box className="col-span-6">
                <Box className="flex justify-end mt-4">
                  <ToggleButtonGroup
                    orientation="horizontal"
                    value={selectedSentimentAnalysisGroup}
                    exclusive
                    onChange={handleSelectedSentimentGroupChange}
                  >
                    <ToggleButton value="Nurse" aria-label="left aligned">
                      Nurse
                    </ToggleButton>
                    <ToggleButton value="EMT" aria-label="centered">
                      EMT
                    </ToggleButton>
                    <ToggleButton value="Paramedic" aria-label="right aligned">
                      Paramedic
                    </ToggleButton>
                    <ToggleButton value="AEMT" aria-label="right aligned">
                      AEMT
                    </ToggleButton>
                    <ToggleButton value="Other" aria-label="right aligned">
                      Other
                    </ToggleButton>
                  </ToggleButtonGroup>
                  <Button
                    color="secondary"
                    variant="contained"
                    sx={{ ml: 2 }}
                    onClick={() => setSentimentAnalysisModalOpen(true)}
                  >
                    View Sentiment Analysis
                  </Button>
                  <Modal
                    open={sentimentAnalysisModalOpen}
                    onClose={() => setSentimentAnalysisModalOpen(false)}
                  >
                    <Box
                      sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: '90vw',
                        bgcolor: 'background.default',
                        border: '2px solid #222',
                        boxShadow: 24,
                        p: 4,
                        overflow: 'scroll',
                        maxHeight: '90vh',
                      }}
                    >
                      <article
                        className="markdown"
                        style={{ minWidth: '100%' }}
                      >
                        <MuiMarkdown>
                          {
                            sentimentAnalyses.find(
                              (x) =>
                                x.entityID === course.id &&
                                x.licenseType ===
                                  selectedSentimentAnalysisGroup,
                            )?.analysisText
                          }
                        </MuiMarkdown>
                      </article>
                    </Box>
                  </Modal>
                </Box>
              </Box>
              <Box className="col-span-6">
                <Paper>
                  <Typography
                    variant="h6"
                    sx={{
                      textAlign: 'center',
                      py: 2,
                      my: 2,
                    }}
                  >
                    Total Learners by Group for {course?.name}
                  </Typography>
                </Paper>
              </Box>
              <Box className="relative col-span-6 min-h-96">
                {!monthlyLearners.length &&
                !yearlyLearners.length &&
                !allTimeLearners.length ? (
                  <Skeleton
                    sx={{
                      bgcolor: 'grey.900',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                    }}
                    variant="rectangular"
                  />
                ) : (
                  <Box sx={{ height: '100%' }}>
                    <BarChart
                      title="Learners by Group"
                      dataset={
                        selectedLearnerChart === 'Monthly'
                          ? monthlyLearners
                          : selectedLearnerChart === 'Yearly'
                            ? yearlyLearners
                            : allTimeLearners
                      }
                      xAxis={[
                        {
                          scaleType: 'band',
                          dataKey:
                            selectedLearnerChart === 'Monthly'
                              ? 'month'
                              : selectedLearnerChart === 'Yearly'
                                ? 'year'
                                : 'allTime',
                        },
                      ]}
                      series={[
                        { dataKey: 'nurse', label: 'Nurse' },
                        { dataKey: 'emt', label: 'EMT' },
                        { dataKey: 'aemt', label: 'AEMT' },
                        { dataKey: 'paramedic', label: 'Paramedic' },
                        { dataKey: 'other', label: 'Other' },
                      ]}
                    />
                  </Box>
                )}
              </Box>

              <Box className="flex justify-center col-span-6">
                {!!monthlyLearners.length &&
                  !!yearlyLearners.length &&
                  !!allTimeLearners.length && (
                    <ToggleButtonGroup
                      value={selectedLearnerChart}
                      exclusive
                      onChange={handleSelectedLearnerChartChange}
                      aria-label="text alignment"
                    >
                      <ToggleButton value="Monthly" aria-label="left aligned">
                        Monthly
                      </ToggleButton>
                      <ToggleButton value="Yearly" aria-label="centered">
                        Yearly
                      </ToggleButton>
                      <ToggleButton value="All Time" aria-label="right aligned">
                        All Time
                      </ToggleButton>
                    </ToggleButtonGroup>
                  )}
              </Box>
              <Box className="col-span-2"></Box>
              <Box className="col-span-6">
                {!!likertAverages.length && (
                  <Paper>
                    <Typography
                      variant="h6"
                      sx={{
                        textAlign: 'center',
                        py: 2,
                        my: 2,
                      }}
                    >
                      Likert Averages for {course?.name}
                    </Typography>
                  </Paper>
                )}
              </Box>
              <Box className="relative col-span-6 min-h-96">
                {!likertAverages.length ? (
                  <Skeleton
                    sx={{
                      bgcolor: 'grey.900',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                    }}
                    variant="rectangular"
                  />
                ) : (
                  <BarChart
                    dataset={
                      selectedLikertChart === '30Day'
                        ? [likertAverages[0]]
                        : selectedLikertChart === 'Year'
                          ? yearLikertAverages
                          : selectedLikertChart === '6Month'
                            ? sixMonthLikertAverages
                            : allTimeLikertAverages
                    }
                    yAxis={[
                      {
                        scaleType: 'band',
                        dataKey: 'monthYear',
                      },
                    ]}
                    series={[
                      {
                        dataKey: 'topicsThoroughAndInteresting',
                        label: 'Topics Thorough and Interesting',
                      },
                      {
                        dataKey: 'presenterGoodUnderstanding',
                        label: 'Presenter Good Understanding',
                      },
                      {
                        dataKey: 'videoQualityProfessional',
                        label: 'Video Quality Professional',
                      },
                      {
                        dataKey: 'benefitCareerGoals',
                        label: 'Benefit Career Goals',
                      },
                      {
                        dataKey: 'knowledgePositiveImpactPractice',
                        label: 'Knowledge Positive Impact Practice',
                      },
                      {
                        dataKey: 'activityPositiveImpactPractice',
                        label: 'Activity Positive Impact Practice',
                      },
                    ]}
                    layout="horizontal"
                    xAxis={[
                      {
                        max: 5,
                        min: 0,
                      },
                    ]}
                    margin={{ left: 100 }}
                  />
                )}
              </Box>
              {!!likertAverages.length && (
                <Box className="col-span-6">
                  <Box className="flex justify-center col-span-6">
                    <ToggleButtonGroup
                      value={selectedLikertChart}
                      exclusive
                      onChange={handleSelectedLikertChartChange}
                      aria-label="text alignment"
                    >
                      <ToggleButton value="30Day" aria-label="left aligned">
                        Previous Month
                      </ToggleButton>
                      <ToggleButton value="6Month" aria-label="centered">
                        Last 6 Months
                      </ToggleButton>
                      <ToggleButton value="Year" aria-label="centered">
                        Past Year
                      </ToggleButton>
                      <ToggleButton value="All Time" aria-label="right aligned">
                        All Time
                      </ToggleButton>
                    </ToggleButtonGroup>
                  </Box>
                  <Box className="grid grid-cols-6">
                    <Box className="flex flex-row items-center justify-center col-span-6 mt-4">
                      <ToggleButtonGroup
                        value={selectedGroup}
                        exclusive
                        onChange={handleSelectedGroupChange}
                        aria-label="text alignment"
                      >
                        <ToggleButton value="Nurse" aria-label="left aligned">
                          Nurse
                        </ToggleButton>
                        <ToggleButton value="EMT" aria-label="centered">
                          EMT
                        </ToggleButton>
                        <ToggleButton
                          value="Paramedic"
                          aria-label="right aligned"
                        >
                          Paramedic
                        </ToggleButton>
                        <ToggleButton value="AEMT" aria-label="right aligned">
                          AEMT
                        </ToggleButton>
                        <ToggleButton value="Other" aria-label="right aligned">
                          Other
                        </ToggleButton>
                      </ToggleButtonGroup>
                    </Box>
                  </Box>
                </Box>
              )}
            </Box>
          </>
        )}
      </Container>
      <Snackbar open={!!errorMessage} onClose={() => setErrorMessage('')}>
        <Alert
          variant="filled"
          onClose={() => setErrorMessage('')}
          severity="error"
        >
          {errorMessage}
        </Alert>
      </Snackbar>
    </>
  )
}
